import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styles from './page.module.css';
import AppBar from '../../../components/AppBar';
import { useTheme } from '../../../context/ThemeProvider';
import { useLocale } from '../../../context/DynamicLocaleProvider';
import ContentGrid from '../../../components/ContentGrid';
import { GlobalMicroButton } from '../../../components/GlobalMicroButton';
import AppFooter from '../../../components/AppFooter';
import { GlobalInput } from '../../../components/GlobalInput';
import { GlobalButton } from '../../../components/GlobalButton';
import { GlobalDropdown } from '../../../components/GlobalDropdown';
import { GlobalCheckbox } from '../../../components/GlobalCheckbox';
import { Project } from '../../../hooks/useProjects';
import { useCompanies } from '../../../hooks/useCompanies';
import { useUser } from '../../../context/UserContext';
import { useToast } from '../../../context/ToastProvider';
import { getProjectById, updateProject, deleteProject } from './controller';
import { doc, getDoc, collection, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import DoubleConfirmationModal from '../../../components/DoubleConfirmationModal';
import GlobalSideMenu from '../../../components/GlobalSideMenu';
import ArchiveChat from '../../../components/ArchiveChat';
import GlobalGptAccessPanel from '../../../components/GlobalGptAccessPanel';
import GlobalSidebar from '../../../components/GlobalSidebar';
import { OverviewTab } from '../../../components/project-tabs/OverviewTab';
import { URSTab } from '../../../components/project-tabs/URSTab';
import { FSTab } from '../../../components/project-tabs/FSTab';
import { SOPsTab } from '../../../components/project-tabs/SOPsTab';
import { TestsTab } from '../../../components/project-tabs/TestsTab';
import { RATab } from '../../../components/project-tabs/RATab';
import { TraceabilityTab } from '../../../components/project-tabs/TraceabilityTab';
import { DocumentsTab } from '../../../components/project-tabs/DocumentsTab';
import AddDocumentSidebar from '../../../components/AddDocumentSidebar';

// Menu mapping for URL query params
const MENU_MAP: Record<string, number> = {
  'overview': 0,
  'documents': 1,
  'urs': 2,
  'fs': 3,
  'risks': 4,
  'tests': 5,
  'sops': 6,
  'traceability': 7,
  'offers': 8,
  'settings': 9
};

const MENU_INDEX_MAP: Record<number, string> = {
  0: 'overview',
  1: 'documents',
  2: 'urs',
  3: 'fs',
  4: 'risks',
  5: 'tests',
  6: 'sops',
  7: 'traceability',
  8: 'offers',
  9: 'settings'
};

export default function ProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const navigate = useNavigate();
  const { t } = useLocale();
  const { role } = useUser();
  const showToast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [companyId, setCompanyId] = useState('');

  // Project Structure Settings (mocked for client iteration)
  const [enabledEntities, setEnabledEntities] = useState<Record<string, boolean>>({
    urs: true,
    fs: true,
    ds: true,
    tests: true,
    risks: true,
    sops: true,
    traceability: true,
  });

  const [entityPrefixes, setEntityPrefixes] = useState<Record<string, string>>({
    urs: 'URS',
    fs: 'FS',
    ds: 'DS',
    tests: 'TEST',
    risks: 'RA',
    sops: 'SOP',
  });

  const [selectedTestTypes, setSelectedTestTypes] = useState<string[]>(['dev-test', 'func-test', 'uat', 'e2e']);
  const [selectedRiskTypes, setSelectedRiskTypes] = useState<string[]>(['ra']);

  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>(['gxp']);

  // Company data for side section
  const [companyData, setCompanyData] = useState<{
    name?: string;
    website?: string;
  } | null>(null);

  // All companies for dropdown
  const { companies, loading: companiesLoading } = useCompanies();

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Side menu state - sync with URL query param
  const [selectedTab, setSelectedTab] = useState(() => {
    const menuParam = searchParams.get('menu');
    if (menuParam && menuParam in MENU_MAP) {
      return MENU_MAP[menuParam];
    }
    return 0;
  });

  // AI Sidebar state
  const [showAiSidebar, setShowAiSidebar] = useState(false);

  // Ask Project AI Sidebar state
  const [showAskProjectAiSidebar, setShowAskProjectAiSidebar] = useState(false);
  const [projectAiAdditionalContext, setProjectAiAdditionalContext] = useState<string>('');

  // Add Document Sidebar state
  const [showAddDocumentSidebar, setShowAddDocumentSidebar] = useState(false);

  // Counts for side menu
  const [ursCount, setUrsCount] = useState(0);
  const [fsCount, setFsCount] = useState(0);
  const [risksCount, setRisksCount] = useState(0);
  const [testsCount, setTestsCount] = useState(0);
  const [sopsCount, setSopsCount] = useState(0);
  const [documentsCount, setDocumentsCount] = useState(0);

  // Check if user is internal_admin
  useEffect(() => {
    if (role !== 'internal_admin') {
      navigate('/home');
    }
  }, [role, navigate]);

  // Fetch project data
  const fetchProjectData = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const projectData = await getProjectById(projectId, t);
      if (!projectData) {
        showToast(t('project_page_not_found') || 'Project not found', 'error');
        navigate('/home');
        return;
      }

      setProject(projectData);
      setProjectName(projectData.projectName || '');
      setProjectDescription(projectData.projectDescription || '');
      setCompanyId(projectData.companyId || '');

      // Fetch company data
      if (projectData.companyId) {
        try {
          const companyRef = doc(db, 'companies', projectData.companyId);
          const companyDoc = await getDoc(companyRef);
          if (companyDoc.exists()) {
            const data = companyDoc.data();
            setCompanyData({
              name: data.name || '',
              website: data.website || ''
            });
          }
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error fetching company data:', error);
          }
        }
      }

    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Error fetching project:', error);
      }
      showToast(t('project_page_load_error') || 'Failed to load project', 'error');
      navigate('/home');
    } finally {
      setLoading(false);
    }
  }, [projectId, t, navigate, showToast]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  // Load counts for side menu with real-time listeners
  useEffect(() => {
    if (!projectId) return;

    // Set up real-time listeners for each collection
    const ursCol = collection(db, 'projects', projectId, 'urs');
    const ursUnsubscribe = onSnapshot(
      ursCol,
      (snapshot) => {
        setUrsCount(snapshot.size);
      },
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error listening to URS count:', error);
        }
      }
    );

    const fsCol = collection(db, 'projects', projectId, 'fs');
    const fsUnsubscribe = onSnapshot(
      fsCol,
      (snapshot) => {
        setFsCount(snapshot.size);
      },
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error listening to FS count:', error);
        }
      }
    );

    const risksCol = collection(db, 'projects', projectId, 'risks');
    const risksUnsubscribe = onSnapshot(
      risksCol,
      (snapshot) => {
        setRisksCount(snapshot.size);
      },
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error listening to Risks count:', error);
        }
      }
    );

    const testsCol = collection(db, 'projects', projectId, 'tests');
    const testsUnsubscribe = onSnapshot(
      testsCol,
      (snapshot) => {
        setTestsCount(snapshot.size);
      },
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error listening to Tests count:', error);
        }
      }
    );

    const sopsCol = collection(db, 'projects', projectId, 'sops');
    const sopsUnsubscribe = onSnapshot(
      sopsCol,
      (snapshot) => {
        setSopsCount(snapshot.size);
      },
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error listening to SOPs count:', error);
        }
      }
    );

    const documentsCol = collection(db, 'projects', projectId, 'documents');
    const documentsUnsubscribe = onSnapshot(
      documentsCol,
      (snapshot) => {
        setDocumentsCount(snapshot.size);
      },
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error listening to Documents count:', error);
        }
      }
    );

    // Cleanup function to unsubscribe from all listeners
    return () => {
      ursUnsubscribe();
      fsUnsubscribe();
      risksUnsubscribe();
      testsUnsubscribe();
      sopsUnsubscribe();
      documentsUnsubscribe();
    };
  }, [projectId]);

  // Sync selectedTab with URL query param changes
  useEffect(() => {
    const menuParam = searchParams.get('menu');
    // Redirect away from offers tab if accessed via URL
    if (menuParam === 'offers') {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('menu');
      setSearchParams(newSearchParams, { replace: true });
      setSelectedTab(0);
      return;
    }
    if (menuParam && menuParam in MENU_MAP) {
      const tabIndex = MENU_MAP[menuParam];
      setSelectedTab(prev => prev !== tabIndex ? tabIndex : prev);
    } else {
      // If no menu param, set to default (overview)
      setSelectedTab(prev => prev !== 0 ? 0 : prev);
    }
  }, [searchParams, setSearchParams]);

  // Update URL when selectedTab changes
  const handleMenuChange = useCallback((index: number) => {
    setSelectedTab(index);
    const menuName = MENU_INDEX_MAP[index];
    // Read from current URL to ensure we have the latest params
    const newSearchParams = new URLSearchParams(window.location.search);
    if (index === 0) {
      // Remove menu param for default (overview)
      newSearchParams.delete('menu');
    } else {
      newSearchParams.set('menu', menuName);
    }
    // Always remove tab param when switching menu items
    newSearchParams.delete('tab');
    setSearchParams(newSearchParams, { replace: true });
  }, [setSearchParams]);

  // Handle entity navigation - switch tab, scroll to entity, expand and highlight it
  const handleEntityNavigation = useCallback((entityId: string, entityType: 'URS' | 'FS' | 'TEST' | 'RISK' | 'SOP') => {
    const tabMap: Record<string, number> = {
      'URS': 1,
      'FS': 2,
      'RISK': 3,
      'TEST': 4,
      'SOP': 5
    };

    const targetTab = tabMap[entityType];
    if (targetTab === undefined) return;

    // Switch to the correct tab
    handleMenuChange(targetTab);

    // Wait for tab to render, then scroll and highlight
    setTimeout(() => {
      const entityElement = document.getElementById(`entity-${entityId}`);
      if (entityElement) {
        // Scroll to entity
        entityElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Expand the entity if it has a toggle (click the card itself since it's clickable)
        const card = entityElement as HTMLElement;
        // Check if card is already expanded by looking for expanded class in details
        const details = card.querySelector('[class*="expanded"]');
        if (!details) {
          // Click the card itself to expand (since the whole card is clickable)
          card.click();
          // Wait for expansion animation before scrolling
          setTimeout(() => {
            entityElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 400);
        } else {
          // Already expanded, just scroll
          entityElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Add highlight class
        entityElement.classList.add('entity-highlight');

        // Remove highlight after animation
        setTimeout(() => {
          entityElement.classList.remove('entity-highlight');
        }, 3000);
      }
    }, 300); // Wait for tab to render
  }, [handleMenuChange]);


  // Update company data when companyId changes
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!companyId) {
        setCompanyData(null);
        return;
      }

      try {
        const companyRef = doc(db, 'companies', companyId);
        const companyDoc = await getDoc(companyRef);
        if (companyDoc.exists()) {
          const data = companyDoc.data();
          setCompanyData({
            name: data.name || '',
            website: data.website || ''
          });
        } else {
          setCompanyData(null);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching company data:', error);
        }
        setCompanyData(null);
      }
    };

    fetchCompanyData();
  }, [companyId]);

  const handleSave = async () => {
    if (!projectId) return;

    setSaving(true);
    try {
      await updateProject(projectId, {
        projectName,
        projectDescription,
        companyId
      });
      showToast(t('project_page_save_success') || 'Project saved successfully', 'success');
      // Keep local state in sync without triggering a full page reload
      setProject(prev => prev ? ({
        ...prev,
        projectName,
        projectDescription,
        companyId
      }) : prev);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Error saving project:', error);
      }
      showToast(t('project_page_save_error') || 'Failed to save project', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!projectId) return;

    setIsDeleting(true);
    try {
      await deleteProject(projectId);
      showToast(t('project_page_delete_success') || 'Project deleted successfully', 'success');
      navigate('/home');
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Project delete failed:', error);
      }
      showToast(t('project_page_delete_error') || 'Failed to delete project', 'error');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%'
      }}>
        <img
          src={isDarkMode ? "/assets/graphics/thescon-loading-white.svg" : "/assets/graphics/thescon-loading.svg"}
          alt="Loading"
          width={120}
          height={120}
        />
      </div>
    );
  }

  if (!project) {
    return (
      <div className={styles.container}>
        <AppBar />
        <ContentGrid rows='64px 136px 1fr 48px'>
          <div className={styles.loadingContainer}>
            <p>{t('project_page_not_found') || 'Project not found'}</p>
          </div>
        </ContentGrid>
      </div>
    );
  }

  return (
    <div>
      {/* App bar */}
      <AppBar />

      {/* Content */}
      <ContentGrid
        mobileRows='32px 1fr auto 48px'
        tabletRows='64px 1fr 48px'
        rows='64px 1fr 48px'
      >

        {/* Toolbar left side */}
        <div className={styles.homeToolbarLeft}>
          {/* Back button */}
          <div className={styles.backButton}>
            <GlobalMicroButton
              title={t('project_page_navigate_back') || 'Back'}
              icon={<img src={isDarkMode ? "/assets/icons/chevron.left.svg" : "/assets/icons/chevron.left.black.svg"} alt="Left" width={8} height={8} />}
              onClick={() => navigate('/home?tab=projects')}
            />
          </div>

          <b>{t('project_page_project_details') || 'Project details'}</b>
        </div>

        {/* Toolbar right side */}
        <div className={styles.homeToolbarRight}>
        </div>

        {/* Side menu */}
        <div className={styles.sideMenuContainer}>
          <GlobalSideMenu
            projectId={projectId}
            companyWebsite={companyData?.website}
            companyName={companyData?.name}
            projectName={projectName}
            items={[
              {
                label: t('project_page_tab_overview') || 'Overview',
                content: null,
                icon: '/assets/icons/rectangle.3.group.white.svg'
              },
              {
                label: t('project_page_tab_documents') || 'Dokumente',
                content: null,
                icon: '/assets/icons/document.on.document.svg',
                count: documentsCount
              },
              {
                label: t('project_page_tab_urs') || 'URS (User requirement specifications)',
                content: null,
                icon: '/assets/icons/checklist.unchecked.white.svg',
                count: ursCount
              },
              {
                label: t('project_page_tab_fs') || 'FS (Functional Specification)',
                content: null,
                icon: '/assets/icons/slider.horizontal.3.svg',
                count: fsCount
              },
              {
                label: t('project_page_tab_ra') || 'RA (Risk Analysis)',
                content: null,
                icon: '/assets/icons/checkmark.shield.svg',
                count: risksCount
              },
              {
                label: t('project_page_tab_tests') || 'Tests',
                content: null,
                icon: '/assets/icons/checkmark.square.svg',
                count: testsCount
              },
              {
                label: t('project_page_tab_sops') || 'SOPs (Standard Operating Processes)',
                content: null,
                icon: '/assets/icons/list.number.svg',
                count: sopsCount
              },
              {
                label: t('project_page_tab_traceability') || 'Traceability Matrix',
                content: null,
                icon: '/assets/icons/point.3.connected.trianglepath.dotted.svg'
              },
              {
                label: t('project_page_tab_offers') || 'Angebote',
                content: null,
                icon: '/assets/icons/tag.svg'
              },
              {
                label: t('project_page_tab_settings') || 'Settings',
                content: null,
                icon: '/assets/icons/gearshape.svg'
              },
              ...(projectId ? [{
                label: t('project_page_project_gpt') || 'Project GPT',
                content: null,
                icon: '/assets/icons/apple.intelligence.svg',
                href: `/thescon-ai?projectId=${projectId}`
              }] : []),
            ].filter((item, index) => index !== 8)} // Hide offers tab (index 8)
            initialIndex={selectedTab === 8 ? 0 : selectedTab > 8 ? selectedTab - 1 : selectedTab}
            onChange={(filteredIndex) => {
              // Map filtered index back to original index
              // If filteredIndex >= 8, it means we clicked settings (which is at index 8 in filtered array, but 9 in original)
              const originalIndex = filteredIndex >= 8 ? filteredIndex + 1 : filteredIndex;
              handleMenuChange(originalIndex);
            }}
          />
        </div>

        {/* Main content area */}
        <div className={styles.mainContent}>

          {/* Tab content */}
          {selectedTab === 0 && projectId && (
            <div className={styles.tabContent}>
              <OverviewTab
                projectName={projectName}
                projectDescription={projectDescription}
                companyWebsite={companyData?.website}
                companyName={companyData?.name}
                documentStatuses={[
                  { type: 'urs', status: 'complete', completion: 100 },
                  { type: 'fs', status: 'in_review', completion: 85 },
                  { type: 'sops', status: 'draft', completion: 40 },
                  { type: 'tests', status: 'pending', completion: 20 },
                  { type: 'ra', status: 'complete', completion: 100 }
                ]}
                complianceScore={85}
                onExport={() => showToast(t('project_tab_exporting') || 'Exporting...', 'info')}
                onAiReview={() => setShowAiSidebar(true)}
                onGenerateReport={() => showToast(t('project_tab_generating_report') || 'Generating report...', 'info')}
                onDeleteProject={() => setShowDeleteModal(true)}
                onAskProjectAi={async () => {
                  if (!projectId) return;

                  try {
                    // Fetch all entities from the project
                    const [ursSnapshot, fsSnapshot, risksSnapshot, testsSnapshot, sopsSnapshot] = await Promise.all([
                      getDocs(collection(db, 'projects', projectId, 'urs')),
                      getDocs(collection(db, 'projects', projectId, 'fs')),
                      getDocs(collection(db, 'projects', projectId, 'risks')),
                      getDocs(collection(db, 'projects', projectId, 'tests')),
                      getDocs(collection(db, 'projects', projectId, 'sops'))
                    ]);

                    // Format entities as context
                    const formatEntity = (doc: any, type: string) => {
                      const data = doc.data();
                      const id = data.id || `${type}-${doc.id.substring(0, 8)}`;
                      let text = `=== ${type} ${id} ===\n`;
                      if (data.title) text += `Title: ${data.title}\n`;
                      if (data.description) text += `Description: ${data.description}\n`;
                      if (data.rationale) text += `Rationale: ${data.rationale}\n`;
                      if (data.design_hint) text += `Design Hint: ${data.design_hint}\n`;
                      if (data.objective) text += `Objective: ${data.objective}\n`;
                      if (data.acceptance_criteria) text += `Acceptance Criteria: ${data.acceptance_criteria}\n`;
                      if (data.preconditions) text += `Preconditions: ${data.preconditions}\n`;
                      if (data.steps) text += `Steps: ${data.steps}\n`;
                      if (data.risk_statement) text += `Risk Statement: ${data.risk_statement}\n`;
                      if (data.cause) text += `Cause: ${data.cause}\n`;
                      if (data.consequence) text += `Consequence: ${data.consequence}\n`;
                      return text;
                    };

                    const contextParts: string[] = [];

                    if (ursSnapshot.size > 0) {
                      contextParts.push('=== USER REQUIREMENT SPECIFICATIONS (URS) ===');
                      ursSnapshot.docs.forEach(doc => {
                        contextParts.push(formatEntity(doc, 'URS'));
                      });
                    }

                    if (fsSnapshot.size > 0) {
                      contextParts.push('\n=== FUNCTIONAL SPECIFICATIONS (FS) ===');
                      fsSnapshot.docs.forEach(doc => {
                        contextParts.push(formatEntity(doc, 'FS'));
                      });
                    }

                    if (risksSnapshot.size > 0) {
                      contextParts.push('\n=== RISKS ===');
                      risksSnapshot.docs.forEach(doc => {
                        contextParts.push(formatEntity(doc, 'RISK'));
                      });
                    }

                    if (testsSnapshot.size > 0) {
                      contextParts.push('\n=== TESTS ===');
                      testsSnapshot.docs.forEach(doc => {
                        contextParts.push(formatEntity(doc, 'TEST'));
                      });
                    }

                    if (sopsSnapshot.size > 0) {
                      contextParts.push('\n=== STANDARD OPERATING PROCEDURES (SOPs) ===');
                      sopsSnapshot.docs.forEach(doc => {
                        contextParts.push(formatEntity(doc, 'SOP'));
                      });
                    }

                    const fullContext = contextParts.join('\n\n');
                    setProjectAiAdditionalContext(fullContext);
                    setShowAskProjectAiSidebar(true);
                  } catch (error) {
                    if (process.env.NODE_ENV !== 'production') {
                      console.error('Error fetching project entities:', error);
                    }
                    showToast(t('project_tab_error_fetching_entities') || 'Error fetching project entities', 'error');
                  }
                }}
              />
            </div>
          )}
          {selectedTab === 1 && projectId && (
            <div className={styles.tabContent}>
              <DocumentsTab
                projectId={projectId}
                onAddDocument={() => setShowAddDocumentSidebar(true)}
              />
            </div>
          )}
          {selectedTab === 2 && projectId && (
            <div className={styles.tabContent}>
              <URSTab
                projectId={projectId}
                onExport={() => showToast(t('project_tab_exporting') || 'Exporting...', 'info')}
                onOpenAiSidebar={() => setShowAiSidebar(true)}
                onNavigateToEntity={handleEntityNavigation}
              />
            </div>
          )}
          {selectedTab === 3 && projectId && (
            <div className={styles.tabContent}>
              <FSTab
                projectId={projectId}
                onExport={() => showToast(t('project_tab_exporting') || 'Exporting...', 'info')}
                onNavigateToEntity={handleEntityNavigation}
              />
            </div>
          )}
          {selectedTab === 4 && projectId && (
            <div className={styles.tabContent}>
              <RATab
                projectId={projectId}
                onExport={() => showToast(t('project_tab_exporting') || 'Exporting...', 'info')}
                onNavigateToEntity={handleEntityNavigation}
              />
            </div>
          )}
          {selectedTab === 5 && projectId && (
            <div className={styles.tabContent}>
              <TestsTab
                projectId={projectId}
                onExport={() => showToast(t('project_tab_exporting') || 'Exporting...', 'info')}
                onNavigateToEntity={handleEntityNavigation}
              />
            </div>
          )}
          {selectedTab === 6 && projectId && (
            <div className={styles.tabContent}>
              <SOPsTab
                projectId={projectId}
                onExport={() => showToast(t('project_tab_exporting') || 'Exporting...', 'info')}
                onNavigateToEntity={handleEntityNavigation}
              />
            </div>
          )}
          {selectedTab === 7 && projectId && (
            <div className={styles.tabContent}>
              <TraceabilityTab
                projectId={projectId}
                onExport={() => showToast(t('project_tab_exporting') || 'Exporting...', 'info')}
                onEntityClick={handleEntityNavigation}
              />
            </div>
          )}
          {/* {selectedTab === 8 && (
            <div className={styles.tabContent}>
              <div className={styles.tabHeader}>
                <h2 className={styles.tabTitle}>
                  {t('project_page_tab_offers') || 'Angebote'}
                </h2>
              </div>
              <GlobalEmptyState />
            </div>
          )} */}
          {selectedTab === 9 && (
            <div className={styles.tabContent}>
              <div className={styles.tabHeader}>
                <h2 className={styles.tabTitle}>
                  {t('project_page_tab_settings') || 'Settings'}
                </h2>
              </div>
              <div className={styles.formSection}>


                <div className={styles.settingsGroup}>
                <label className={styles.settingsLabel}>
                  Enabled Entities
                </label>

                <div className={styles.formRow}>
                  <GlobalInput
                    label={t('project_page_name') || 'Project name'}
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    className={styles.formInput}
                  />
                  <GlobalInput
                    label={t('project_page_description') || 'Description'}
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    className={styles.formInput}
                  />
                </div>
                <div className={styles.formRow}>
                  <GlobalDropdown
                    label={t('project_modal_company_label') || 'Company'}
                    value={companyId}
                    onChange={(value) => setCompanyId(value)}
                    options={companies.map(company => ({
                      value: company.id,
                      label: company.name
                    }))}
                    placeholder={companiesLoading ? (t('project_modal_company_loading') || 'Loading companies...') : (t('project_modal_company_placeholder') || 'Select company')}
                    disabled={companiesLoading}
                    className={styles.formInput}
                  />
                </div>
                </div>
              </div>


              {/* Enabled Entities */}
              <div className={styles.settingsGroup}>
                <label className={styles.settingsLabel}>
                  Enabled Entities
                </label>
                <div className={styles.checkboxGrid}>
                  {Object.entries({
                    urs: 'URS',
                    fs: 'FS',
                    ds: 'DS',
                    tests: 'Tests',
                    risks: 'Risks',
                    sops: 'SOPs',
                  }).map(([key, label]) => (
                    <GlobalCheckbox
                      key={key}
                      label={label}
                      checked={enabledEntities[key] || false}
                      onChange={(e) => setEnabledEntities(prev => ({
                        ...prev,
                        [key]: e.target.checked
                      }))}
                    />
                  ))}
                </div>
              </div>

              {/* Entity Prefixes */}
              {Object.values(enabledEntities).some(enabled => enabled) && (
                <div className={styles.settingsGroup}>
                  <label className={styles.settingsLabel}>
                    Entity Prefixes
                  </label>
                  <div className={styles.formRow}>
                    {Object.entries({
                      urs: 'URS Prefix',
                      fs: 'FS Prefix',
                      ds: 'DS Prefix',
                      tests: 'Tests Prefix',
                      risks: 'Risks Prefix',
                      sops: 'SOPs Prefix',
                    })
                      .filter(([key]) => enabledEntities[key])
                      .map(([key, label]) => (
                        <GlobalInput
                          key={key}
                          label={label}
                          value={entityPrefixes[key] || ''}
                          onChange={(e) => setEntityPrefixes(prev => ({
                            ...prev,
                            [key]: e.target.value
                          }))}
                          className={styles.formInput}
                        />
                      ))}
                  </div>
                </div>
              )}

              {/* Test Types */}
              <div className={styles.settingsGroup}>
                <label className={styles.settingsLabel}>
                  Test Scope
                </label>
                <div className={styles.checkboxList}>
                  {[
                    { id: 'dev-test', label: 'Developer Test (DT)' },
                    { id: 'func-test', label: 'Functional Test (FUT)' },
                    { id: 'uat', label: 'User Acceptance Test (UAT)' },
                    { id: 'e2e', label: 'E2E Test (UATs, URS & Processes)' },
                  ].map((testType) => (
                    <GlobalCheckbox
                      key={testType.id}
                      label={testType.label}
                      checked={selectedTestTypes.includes(testType.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTestTypes(prev => [...prev, testType.id]);
                        } else {
                          setSelectedTestTypes(prev => prev.filter(id => id !== testType.id));
                        }
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Risk Types */}
              {enabledEntities.risks && (
                <div className={styles.settingsGroup}>
                  <label className={styles.settingsLabel}>
                    Risk Framing
                  </label>
                  <div className={styles.checkboxList}>
                    {[
                      { id: 'ra', label: 'Risk Assessment (RA)' },
                      { id: 'pra', label: 'Process Risk Assessment (PRA)' },
                      { id: 'fra', label: 'Functional Risk Assessment (FRA)' },
                    ].map((riskType) => (
                      <GlobalCheckbox
                        key={riskType.id}
                        label={riskType.label}
                        checked={selectedRiskTypes.includes(riskType.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRiskTypes(prev => [...prev, riskType.id]);
                          } else {
                            setSelectedRiskTypes(prev => prev.filter(id => id !== riskType.id));
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Frameworks */}
              <div className={styles.settingsGroup}>
                <label className={styles.settingsLabel}>
                  Frameworks
                </label>
                <div className={styles.checkboxList}>
                  {['GxP', 'ISO 13485', 'FDA 21 CFR Part 11', 'EU MDR', 'IEC 62304'].map((framework) => (
                    <GlobalCheckbox
                      key={framework}
                      label={framework}
                      checked={selectedFrameworks.includes(framework.toLowerCase().replace(/\s+/g, '-'))}
                      onChange={(e) => {
                        const frameworkId = framework.toLowerCase().replace(/\s+/g, '-');
                        if (e.target.checked) {
                          setSelectedFrameworks(prev => [...prev, frameworkId]);
                        } else {
                          setSelectedFrameworks(prev => prev.filter(f => f !== frameworkId));
                        }
                      }}
                    />
                  ))}
                </div>
              </div>


              {/* Save Button at Bottom */}
              <div className={`${styles.saveButtonContainer} ${styles.saveButtonContainerBottom}`}>
                <GlobalButton
                  onClick={handleSave}
                  disabled={saving}
                  isLoading={saving}
                >
                  {t('project_page_save') || 'Save'}
                </GlobalButton>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <AppFooter inline />

      </ContentGrid>

      {/* AI Expert Sidebar */}
      <GlobalSidebar
        isOpen={showAiSidebar}
        onClose={() => setShowAiSidebar(false)}
      >
        <div>
          {/* Header */}
          <div className={styles.aiExpertSidebarHeader}>
            <img src={isDarkMode ? "/assets/identity/logo-dark.svg" : "/assets/identity/logo.svg"} alt="Thescon" width={16} height={16} />
            <span>|</span>
            <h2>AI</h2>
          </div>

          {/* Content */}
          <GlobalGptAccessPanel hideBorderTop projectId={projectId} />
        </div>
      </GlobalSidebar>

      {/* Ask Project AI Sidebar */}
      {projectId && (
        <GlobalSidebar
          isOpen={showAskProjectAiSidebar}
          onClose={() => {
            setShowAskProjectAiSidebar(false);
            setProjectAiAdditionalContext('');
          }}
        >
          <div>
            {/* Header */}
            <div className={styles.aiExpertSidebarHeader}>
              <img src={isDarkMode ? "/assets/identity/logo-dark.svg" : "/assets/identity/logo.svg"} alt="Thescon" width={16} height={16} />
              <span>|</span>
              <h2>AI</h2>
            </div>

            {/* Content */}
            <ArchiveChat
              hideBorderTop={true}
              enabled={true}
              title={t('project_tab_ask_project_ai_title') || 'Ask Project AI'}
              paragraph={t('project_tab_ask_project_ai_intro') || 'Ask questions about this project. The AI will search through all project documents, URS, FS, RISKs, Tests, and SOPs to provide comprehensive answers.'}
              additionalContext={projectAiAdditionalContext}
              projectId={projectId}
            />
          </div>
        </GlobalSidebar>
      )}

      {/* Add Document Sidebar */}
      {projectId && (
        <AddDocumentSidebar
          isOpen={showAddDocumentSidebar}
          onClose={() => setShowAddDocumentSidebar(false)}
          projectId={projectId}
          onSuccess={() => {
            // Documents will refresh automatically via the useProjectDocuments hook
          }}
        />
      )}

      {/* Delete confirmation modal */}
      <DoubleConfirmationModal
        isOpen={showDeleteModal}
        title={t('project_page_delete_confirm_title') || `Delete "${projectName || t('project_page_this_project') || 'this project'}"?`}
        paragraph={t('project_page_delete_confirm_paragraph') || 'This action cannot be undone. All data associated with this project will be permanently deleted.'}
        primaryLabel={t('project_page_confirm_deletion') || 'Confirm Deletion'}
        primaryIcon="/assets/icons/trash.red.svg"
        secondaryLabel={t('project_page_cancel') || 'Cancel'}
        secondaryIcon="/assets/icons/xmark.svg"
        onPrimary={handleDelete}
        onSecondary={handleCancelDelete}
        primaryDestructive
        loading={isDeleting}
      />
    </div>
  );
}

